<template>
  <o-dialog
    ref="dialog"
    :title="$t('note.add')"
    :info="$t('note.addInfo', { name: customer.displayName })"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('note.add'), type: 'confirm', callback: confirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="mt-4 text-left ">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('note.type')" :name="$t('note.type')" rules="required" class="block pb-3">
          <segment-field v-model="type" :options="customerNoteTypeOptions" :status="status" />
        </o-field-group>

        <o-field-group v-slot="{ status }" :label="$t('general.note')" :name="$t('general.note')" rules="required" class="block pb-3">
          <text-area-field v-model="note" rows="6" :status="status" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { SegmentField, TextAreaField } from '@/components/fields'

import { CustomerNoteType, CustomerNoteTypeLabels } from '~/_api/models/customerNote'

import type { Customer } from '~/_api/models/customer'

interface Props {
  customer: Customer
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus, $auth } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const type = ref(CustomerNoteType.note)
const note = ref('')

const customerNoteTypeOptions = computed(() => {
  return Object.values(CustomerNoteType)
    .filter((value): value is CustomerNoteType => typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(CustomerNoteTypeLabels[value]),
        value,
      }
    })
})

const isCustomerNoteTypeNote = computed(() => {
  return type.value === CustomerNoteType.note
})

const isCustomerNoteTypeForumla = computed(() => {
  return type.value === CustomerNoteType.formula
})

const isCustomerNoteTypeAlert = computed(() => {
  return type.value === CustomerNoteType.alert
})

watch(type, () => {
  if (isCustomerNoteTypeNote.value || isCustomerNoteTypeAlert.value) {
    note.value = ''
  }

  if (isCustomerNoteTypeForumla.value) {
    note.value = $auth.info.location.notesFormulaTemplate
  }
}, { deep: true })

const open = (noteType = CustomerNoteType.note) => {
  type.value = noteType
  dialog.value.open()
}

const confirm = async () => {
  try {
    const defaultsResponse = await $api.customerNote.defaults()
    const customerNote = defaultsResponse.data

    if (!customerNote) return false

    customerNote.content = note.value
    customerNote.idCustomer = props.customer.idCustomer
    customerNote.type = type.value

    const saveResponse = await $api.customerNote.save(customerNote)
    const savedCustomerNote = saveResponse.data

    if (savedCustomerNote) {
      emit('success')

      $bus.emit.created('CustomerNote', savedCustomerNote)
      $bus.emit.updated('Customer', { idCustomer: Number(savedCustomerNote.idCustomer) })

      $notification.toast({
        title: $i18n.t('note.addSuccess2'),
        text: $i18n.t('note.addSuccessInfo', { name: props.customer.displayName }),
        variant: 'success',
      })

      return savedCustomerNote
    }
  } catch (e) {
    console.log(e)
  }
}

const validate = () => {
  return form.value.validate()
}

defineExpose({ open })
</script>
